import { LazyAsynchronousFunction } from "./LazyAsyncronousFunction";
import { IPromisesGroup } from "./IPromisesGroup";
import { IPromise } from "./IPromise";
import { IPromiseResult, PromiseResultType } from "./IPromiseResult";
import { Assert } from "../Utils/Assert";

enum CallbackType {
    OnSuccess,
    OnError,
    OnFinish,
}

interface IPair<TKey, TValue> {
    key: TKey;
    value: TValue;
}

interface IPromisesGroupLazyAsynchronousFunction<TPromisesGroupResult, TPromisesGroupErrors, TFuncResult, TFuncErrors> {
    lazyFunction: LazyAsynchronousFunction<TFuncResult, TFuncErrors>;
    resultPopulator: (funcResult: TFuncResult, currentGroupResult: TPromisesGroupResult) => void;
    errorsPopulator: (funcErrors: TFuncErrors, currentGroupErrors: TPromisesGroupErrors) => void;
}

export class PromisesGroup<TPromisesGroupResult, TPromisesGroupErrors>
    implements IPromisesGroup<TPromisesGroupResult, TPromisesGroupErrors> {
    private _result: TPromisesGroupResult;
    private _errors: TPromisesGroupErrors;

    private _callbacks: IPair<CallbackType, (result?: any) => void>[];
    private _underlyingAsyncFunctions: IPromisesGroupLazyAsynchronousFunction<
        TPromisesGroupResult,
        TPromisesGroupErrors,
        any,
        any
    >[];

    constructor() {
        this._callbacks = [];
        this._result = {} as TPromisesGroupResult;
        this._errors = {} as TPromisesGroupErrors;
        this._underlyingAsyncFunctions = [];
    }

    public addPromiseFunction<TFuncResult, TFuncErrors>(
        asyncFunction: () => IPromise<TFuncResult, TFuncErrors>,
        resultPopulator: (funcResult: TFuncResult, currentGroupResult: TPromisesGroupResult) => void = () => {
            /* empty */
        },
        errorsPopulator: (funcErrors: TFuncErrors, currentGroupErrors: TPromisesGroupErrors) => void = () => {
            /* empty */
        }
    ): void {
        this._underlyingAsyncFunctions.push({
            lazyFunction: new LazyAsynchronousFunction(asyncFunction),
            resultPopulator,
            errorsPopulator,
        });
    }

    public run(): PromisesGroup<TPromisesGroupResult, TPromisesGroupErrors> {
        const allUnderlyingFunctionsHasNotStarted = this._underlyingAsyncFunctions.every(
            (x: IPromisesGroupLazyAsynchronousFunction<TPromisesGroupResult, TPromisesGroupErrors, any, any>) =>
                !x.lazyFunction.hasAlreadyStarted()
        );
        Assert(allUnderlyingFunctionsHasNotStarted, "Attempting to run promise group that has already run");

        this._underlyingAsyncFunctions.map(
            (
                underlyingAsyncFunction: IPromisesGroupLazyAsynchronousFunction<
                    TPromisesGroupResult,
                    TPromisesGroupErrors,
                    any,
                    any
                >
            ) => {
                underlyingAsyncFunction.lazyFunction
                    .run()
                    .onSuccess((funcResult: any) => {
                        underlyingAsyncFunction.resultPopulator(funcResult, this._result);
                        this.onUnderlyingPromiseFinished();
                    })
                    .onError((funcErrors: any) => {
                        underlyingAsyncFunction.errorsPopulator(funcErrors, this._errors);
                        this.onUnderlyingPromiseFinished();
                    });
            }
        );

        return this;
    }

    public getResult(): IPromiseResult<TPromisesGroupResult, TPromisesGroupErrors> {
        Assert(this.isFinished(), "Attempting to get result of not finished promises group");

        return {
            type: this._underlyingAsyncFunctions.every(
                (x: IPromisesGroupLazyAsynchronousFunction<TPromisesGroupResult, TPromisesGroupErrors, any, any>) =>
                    x.lazyFunction.getResult().getResult().type === PromiseResultType.Success
            )
                ? PromiseResultType.Success
                : PromiseResultType.Error,
            errors: this._errors,
            result: this._result,
        };
    }

    public isFinished(): boolean {
        return this._underlyingAsyncFunctions.every(
            (x: IPromisesGroupLazyAsynchronousFunction<TPromisesGroupResult, TPromisesGroupErrors, any, any>) =>
                x.lazyFunction.hasAlreadyFinished()
        );
    }

    public onSuccess(
        callback: (param: TPromisesGroupResult) => void
    ): IPromise<TPromisesGroupResult, TPromisesGroupErrors> {
        Assert(!this.isFinished(), "Attempting to assign onSuccess callback to finished PromisesGroup");
        this._callbacks.push({ key: CallbackType.OnSuccess, value: callback });
        return this;
    }

    public onError(
        callback: (param: TPromisesGroupErrors) => void
    ): IPromise<TPromisesGroupResult, TPromisesGroupErrors> {
        Assert(!this.isFinished(), "Attempting to assign onError callback to finished PromisesGroup");
        this._callbacks.push({ key: CallbackType.OnError, value: callback });

        return this;
    }

    public onFinish(callback: () => void): IPromise<TPromisesGroupResult, TPromisesGroupErrors> {
        Assert(!this.isFinished(), "Attempting to assign onFinished callback to finished PromisesGroup");
        this._callbacks.push({ key: CallbackType.OnFinish, value: callback });

        return this;
    }

    private onUnderlyingPromiseFinished(): void {
        if (!this.isFinished()) {
            return;
        }

        const result = this.getResult();
        result.type === PromiseResultType.Success
            ? this._callbacks
                  .filter((x: IPair<CallbackType, (result?: any) => void>) =>
                      [CallbackType.OnSuccess, CallbackType.OnFinish].some((y: CallbackType) => y === x.key)
                  )
                  .forEach((x: IPair<CallbackType, (result?: any) => void>) => x.value(result.result))
            : this._callbacks
                  .filter((x: IPair<CallbackType, (result?: any) => void>) =>
                      [CallbackType.OnError, CallbackType.OnFinish].some((y: CallbackType) => y === x.key)
                  )
                  .forEach((x: IPair<CallbackType, (result?: any) => void>) => x.value(result.errors));
    }
}
